import type { FileViewedState, PullRequestChangedFile } from '@octokit/graphql-schema';
import { queryOptions, useMutation } from '@tanstack/react-query';

import { octogql, queryClient } from '@/lib/clients';

export const rawFileContentQuery = (owner: string, repo: string, sha: string, filePath: string) =>
  queryOptions({
    queryKey: [owner, repo, 'rawFile', sha, filePath] as const,
    queryFn: async () => {
      const response = await fetch(
        `/api/github/raw/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/${encodeURIComponent(sha)}/${encodeURIComponent(filePath)}`,
        {
          credentials: 'include',
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }

      return response.text();
    },
    staleTime: 1000 * 60 * 5,
  });

export const viewedFilesQuery = (owner: string, repo: string, pull: number) =>
  queryOptions({
    queryKey: [owner, repo, 'pulls', pull, 'viewedFiles'],
    queryFn: async () => {
      const response = await octogql.graphql.paginate<{
        repository: { pullRequest: { files: { nodes: Pick<PullRequestChangedFile, 'path' | 'viewerViewedState'>[] } } };
      }>(
        `
        query ViewedFiles($owner: String!, $repo: String!, $pull: Int!, $cursor: String) {
          repository(owner: $owner, name: $repo) {
            pullRequest(number: $pull) {
              files(first: 100, after: $cursor) {
                nodes {
                  path
                  viewerViewedState
                }
                pageInfo {
                  hasNextPage
                  endCursor
                }
              }
            }
          }
        }
      `,
        { owner, repo, pull }
      );
      return response.repository.pullRequest.files.nodes;
    },
  });

export const useFileMutations = (owner: string, repo: string, pull: number, node_id: string) => {
  const { mutate: markFileAsViewed } = useMutation({
    mutationFn: async ({ path }: { path: string }) => {
      const response = await octogql.graphql(
        `
        mutation MarkFileAsViewed($pullRequestId: ID!, $path: String!) {
          markFileAsViewed(input: {
            pullRequestId: $pullRequestId,
            path: $path
          }) {
            pullRequest {
              id
            }
          }
        }
        `,
        {
          pullRequestId: node_id,
          path,
        }
      );
      return response;
    },
    onMutate: ({ path }) => {
      queryClient.setQueryData(viewedFilesQuery(owner, repo, pull).queryKey, (old) => {
        return old?.map((file) => {
          if (file.path === path) {
            return { ...file, viewerViewedState: 'VIEWED' as FileViewedState };
          }
          return file;
        });
      });
    },
    onError: (error, vars) => {
      console.error('error marking file as viewed', error);
      queryClient.setQueryData(viewedFilesQuery(owner, repo, pull).queryKey, (old) => {
        return old?.map((file) => {
          if (file.path === vars.path) {
            return { ...file, viewerViewedState: 'UNVIEWED' as FileViewedState };
          }
          return file;
        });
      });
    },
  });

  const { mutate: unmarkFileAsViewed } = useMutation({
    mutationFn: async ({ path }: { path: string }) => {
      const response = await octogql.graphql(
        `
        mutation UnmarkFileAsViewed($pullRequestId: ID!, $path: String!) {
          unmarkFileAsViewed(input: {
            pullRequestId: $pullRequestId,
            path: $path
          }) {
            pullRequest {
              id
            }
          }
        }
        `,
        {
          pullRequestId: node_id,
          path,
        }
      );

      return response;
    },
    onMutate: ({ path }) => {
      queryClient.setQueryData(viewedFilesQuery(owner, repo, pull).queryKey, (old) => {
        return old?.map((file) => {
          if (file.path === path) {
            return { ...file, viewerViewedState: 'UNVIEWED' as FileViewedState };
          }
          return file;
        });
      });
    },
    onError: (error, vars) => {
      console.error('error unmarking file as viewed', error);
      queryClient.setQueryData(viewedFilesQuery(owner, repo, pull).queryKey, (old) => {
        return old?.map((file) => {
          if (file.path === vars.path) {
            return { ...file, viewerViewedState: 'VIEWED' as FileViewedState };
          }
          return file;
        });
      });
    },
  });

  return { markFileAsViewed, unmarkFileAsViewed };
};
